import classNames from 'classnames';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXXL, LabelL } from '@sorare/core/src/atoms/typography';
import { RewardImageShine } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation/index.tsx';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import { SetColorTheme } from '@sorare/core/src/set/SetColorTheme/index.tsx';
import { useRewardSuccessPopupBackground } from '@sorare/core/src/set/branding/useRewardSuccessPopupBackground.ts';

import { ButtonsWrapper, Root } from '../styles.tsx';

export type RewardGroupDisplayProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  amount?: ReactNode;
  action?: ReactNode;
  rgbColor?: string;
  children?: ReactNode;
  disclaimer?: ReactNode;
  imgUrls: string[];
};

const StyledRoot = styled(Root)`
  --image-size: calc(22 * var(--unit));
`;

const firstImageEntrance = keyframes`
  from {
    transform: scale(1.67);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const nextImageEntrance = keyframes`
  from {
    width: 0;
    opacity: 0;
  }
  40% {
    opacity: 0;
  }
  to {
    width: var(--image-size);
    opacity: 1;
  }
`;

const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  max-width: 100%;
`;

const Images = styled(Horizontal).attrs({ center: true })`
  gap: calc(2 * var(--unit));
  max-width: 100%;
`;

const ImageWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  min-width: 0;
  width: var(--image-size);
  height: var(--image-size);
  transition:
    width 0.6s var(--ease-in-out-quart),
    height 0.6s var(--ease-in-out-quart);

  &.first {
    opacity: 0;
    animation: ${firstImageEntrance} 0.6s var(--ease-in-out-quart) forwards;
    animation-delay: 0.5s;
  }
  &.next {
    animation: ${nextImageEntrance} 0.6s var(--ease-in-out-quart);
  }
  &.past {
    width: calc(var(--image-size) * 0.7);
    height: calc(var(--image-size) * 0.7);
  }
`;

const TransitionRewardImage = styled.img`
  width: 100%;
  height: 100%;
  min-height: 0;
  object-fit: contain;
`;

const TransitionTextArea = styled(Vertical).attrs({ gap: 2, center: true })`
  opacity: 0;
  animation: ${fadeIn} 0.65s var(--ease-in-out-quart) forwards;
  animation-delay: 0.55s;
`;

const messages = defineMessages({
  andMore: {
    defaultMessage: 'And...',
  },
});

type MultiRewardUnlockedStoryProps = {
  header?: ReactNode;
  onClose: () => void;
  groups: RewardGroupDisplayProps[];
  showContinueButton?: boolean;
  continueButtonColor?: 'primary' | 'secondary';
  hideBackground?: boolean;
  useSetBackground?: boolean;
};

export const MultiRewardUnlockedStory = ({
  header,
  onClose,
  groups,
  showContinueButton = true,
  continueButtonColor = 'primary',
  hideBackground,
  useSetBackground,
}: MultiRewardUnlockedStoryProps) => {
  const isSeriesNav = useSeriesNav();
  const backgroundUrl = useRewardSuccessPopupBackground();
  const [step, setStep] = useState(0);
  const [shiningSteps, setShiningSteps] = useState<number[]>([]);

  const currentGroup = groups.at(step);

  const shouldUseSetBackground =
    useSetBackground || (isSeriesNav && !!backgroundUrl);
  const shouldHideBackground = hideBackground || shouldUseSetBackground;

  if (!currentGroup) {
    return null;
  }

  const isLastStep = step === groups.length - 1;

  return (
    <StyledRoot
      style={
        {
          '--rgb-color': currentGroup.rgbColor || 'var(--c-rgb-green-400)',
          ...(shouldUseSetBackground && backgroundUrl
            ? {
                background: `url(${backgroundUrl}) center / cover no-repeat`,
              }
            : {}),
        } as unknown as CSSProperties
      }
      className={classNames({ hideBackground: shouldHideBackground })}
    >
      {header}
      <Content className="text-center">
        <Images>
          {groups.slice(0, step + 1).map((group, index) => (
            <ImageWrapper
              // groups never reorder, we only append revealed ones
              // oxlint-disable-next-line react/no-array-index-key
              key={index}
              className={classNames(index === 0 ? 'first' : 'next', {
                past: index !== step,
              })}
              onAnimationEnd={() => {
                setShiningSteps(steps =>
                  steps.includes(index) ? steps : [...steps, index]
                );
              }}
            >
              <RewardImageShine
                className={classNames({
                  revealed: shiningSteps.includes(index),
                })}
                style={
                  {
                    '--mask-image': `url(${group.imgUrls[0]})`,
                  } as CSSProperties
                }
              />
              {group.imgUrls.map(imgUrl => (
                <TransitionRewardImage key={imgUrl} src={imgUrl} alt="" />
              ))}
            </ImageWrapper>
          ))}
        </Images>
        <SetColorTheme disabled={!shouldUseSetBackground}>
          <TransitionTextArea>
            {!!currentGroup.amount && (
              <BrandXXL color="var(--c-nd-600)">{currentGroup.amount}</BrandXXL>
            )}
            <BrandL color="var(--c-white)">{currentGroup.title}</BrandL>
            {!!currentGroup.subtitle && (
              <LabelL color="var(--c-nd-600)">{currentGroup.subtitle}</LabelL>
            )}
          </TransitionTextArea>
        </SetColorTheme>
      </Content>
      <ButtonsWrapper>
        <SetColorTheme disabled={!shouldUseSetBackground}>
          <div>{currentGroup.disclaimer}</div>
        </SetColorTheme>
        {isLastStep ? (
          <>
            {currentGroup.action}
            {!!showContinueButton && (
              <Button
                size="large"
                onClick={() => onClose?.()}
                color={continueButtonColor}
                disableDebounce
              >
                <FormattedMessage {...glossary.continue} />
              </Button>
            )}
          </>
        ) : (
          <Button
            size="large"
            color="secondary"
            onClick={() => setStep(step + 1)}
            disableDebounce
          >
            <FormattedMessage {...messages.andMore} />
          </Button>
        )}
      </ButtonsWrapper>
    </StyledRoot>
  );
};
